/**
 * Hooks compartidos: enrutado por hash, suscripcion al tiempo real y utilidades
 * de interfaz (valor con retardo y pila de avisos).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { realtimeHub } from './sse.ts';
import type { RealtimeListener, SseStatus } from './sse.ts';
import type { Route } from './state.ts';
import { parseRoute, routeHash } from './state.ts';

/** Ruta actual, derivada del hash de la URL (enrutado propio, sin libreria). */
export function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));

  useEffect(() => {
    const onChange = (): void => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    onChange();
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return route;
}

/** Cambia de pantalla escribiendo en el hash (dispara `hashchange`). */
export function navigate(route: Route): void {
  const hash = routeHash(route);
  if (window.location.hash === hash) return;
  window.location.hash = hash;
}

/** Se suscribe a los eventos SSE; el listener siempre ve el estado mas reciente. */
export function useRealtime(listener: RealtimeListener): void {
  const ref = useRef(listener);
  useEffect(() => {
    ref.current = listener;
  }, [listener]);
  useEffect(() => realtimeHub.subscribe((event) => ref.current(event)), []);
}

export function useRealtimeStatus(): SseStatus {
  const [status, setStatus] = useState<SseStatus>(() => realtimeHub.getStatus());
  useEffect(() => realtimeHub.subscribeStatus(setStatus), []);
  return status;
}

/** Valor con retardo, para no consultar en cada tecla del buscador. */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export type NoticeKind = 'info' | 'success' | 'warning' | 'error';
export type Notice = { id: number; kind: NoticeKind; text: string };

export type NoticesApi = {
  notices: Notice[];
  push: (kind: NoticeKind, text: string) => void;
  dismiss: (id: number) => void;
};

/** Pila de avisos efimeros (nunca se usa `alert()`). */
export function useNotices(autoDismissMs = 8000): NoticesApi {
  const [notices, setNotices] = useState<Notice[]>([]);
  const counter = useRef(0);
  const timers = useRef<number[]>([]);

  const dismiss = useCallback((id: number): void => {
    setNotices((prev) => prev.filter((notice) => notice.id !== id));
  }, []);

  const push = useCallback(
    (kind: NoticeKind, text: string): void => {
      counter.current += 1;
      const id = counter.current;
      setNotices((prev) => [...prev.slice(-3), { id, kind, text }]);
      const timer = window.setTimeout(() => dismiss(id), autoDismissMs);
      timers.current.push(timer);
    },
    [autoDismissMs, dismiss],
  );

  useEffect(
    () => () => {
      for (const timer of timers.current) window.clearTimeout(timer);
    },
    [],
  );

  return useMemo(() => ({ notices, push, dismiss }), [notices, push, dismiss]);
}
